import { createElement } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { getApiUrl, saveReturnPath } from '@/lib/api';
import { getAuthProviderIcon } from '@/lib/plugin-registry';

interface SignInProviderRowProps {
  /** Provider name as the API knows it (e.g. `github`, `google`, `linear`, `slack`). */
  name: string;
  /** Display label as advertised by `/auth/me` (`GitHub`, `Google`, …). */
  label: string;
  /**
   * Whether to remember the current path so the auth callback can return the
   * user here (login flow). Defaults to `true`; set `false` for the setup
   * wizard so the callback always lands back on `/setup`.
   */
  rememberReturnPath?: boolean;
}

/**
 * One row in a list of OAuth providers offered to the user, modelled after
 * the wizard's auth-provider configuration rows so the "log in" step lines
 * up visually with the "configure providers" step that precedes it.
 *
 * Each row is a self-contained card with the provider's icon and name on
 * the left and a primary "Sign in" CTA on the right that hands off to the
 * provider's OAuth start route.
 */
export function SignInProviderRow({
  name,
  label,
  rememberReturnPath = true,
}: SignInProviderRowProps) {
  const Icon = getAuthProviderIcon(name);
  return (
    <div
      className="rounded-md border border-border/60 p-4"
      data-testid={`sign-in-provider-row-${name}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/40">
            {Icon ? (
              createElement(Icon, { className: 'size-5' })
            ) : (
              <img
                src={`/plugin-icons/plugin-icon-auth-${name}.png`}
                alt=""
                className="size-5 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </div>
          <PanelTitle as="h3">{label}</PanelTitle>
        </div>
        <Button asChild size="sm" data-testid={`sign-in-with-${name}`}>
          <a
            href={getApiUrl(`/auth/${name}`)}
            onClick={rememberReturnPath ? saveReturnPath : undefined}
          >
            Sign in
            <ArrowRight className="icon-xs" />
          </a>
        </Button>
      </div>
    </div>
  );
}
